import { toast } from 'react-toastify';
import {
  ApiError,
  GenderType,
  ModelTag,
  PromptType,
  RoleType,
  SuicideLabel,
} from './types';

const JWT_KEY = 'jwt';
const UNKNOWN_ERR_MSG = 'An unknown error has occurred!';

export const APP = {
  TITLE: {
    DEFAULT: 'Dialogue AI',
    SHORT: 'Dialogue',
    FULL: 'Dual Dialogue AI Demonstrator',
  },
};

export const ROLE_LABELS: {
  [role in RoleType]: {
    tag: string;
    label: string;
    color:
      | 'default'
      | 'success'
      | 'primary'
      | 'secondary'
      | 'warning'
      | 'danger';
  };
} = {
  admin: { tag: 'ADMIN', label: 'Admin', color: 'warning' },
  user_new: { tag: 'NEW', label: 'Unassigned', color: 'danger' },
  user_sos: { tag: 'SOS', label: 'SOS', color: 'default' },
  user_lets_talk: { tag: 'LT', label: "Let's Talk", color: 'default' },
  user_public: { tag: 'PUBLIC', label: 'Public', color: 'default' },
  user_trainee: { tag: 'TRAINEE', label: 'Trainee', color: 'default' },
  user_scs: { tag: 'SCS', label: 'SCS', color: 'default' },
};

export const GENDER_LABELS: {
  [gender in GenderType]: {
    label: string;
  };
} = {
  female: { label: 'Female' },
  male: { label: 'Male' },
  nonbinary: { label: 'Non-Binary' },
  undisclosed: { label: 'Undisclosed' },
};

/**
 * Keys for `motion` properties.
 */
export const MOTION_KEYS = {
  INITIAL: 'initial',
  ANIMATE: 'visible',
  EXIT: 'exiting',
};

/**
 * Checks a `fetch` response for errors.
 *
 * @param fetchPromise Response promise from `fetch`.
 * @param options Optional toast notifications on error.
 * @returns The passed-through `Response` if successful.
 */
export async function checkResponse(
  fetchPromise: Promise<Response>,
  options?: {
    toastOnApiError?: boolean;
    toastOnFetchError?: boolean;
    toastOnFetchErrorMessage?: string;
  }
) {
  try {
    const res = await fetchPromise;
    if (res.ok) {
      return res;
    }
    if (options?.toastOnApiError) {
      const error: ApiError = await res.json();
      toast.error(error.detail || UNKNOWN_ERR_MSG);
    }
  } catch (e) {
    console.error(e);
    if (options?.toastOnFetchError) {
      toast.error(options.toastOnFetchErrorMessage || UNKNOWN_ERR_MSG);
    }
  }
}

/**
 * `fetch` wrapper with authorization header.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit
) {
  const jwt = loadJwt();
  const { ...options } = init || {};
  if (jwt) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${jwt}`,
    };
  } else {
    options.credentials = 'include';
  }
  return fetch(input, options);
}

/**
 * Loads the JSON web token from local storage.
 *
 * @returns The loaded JWT, if any.
 */
export function loadJwt() {
  return localStorage.getItem(JWT_KEY);
}

/**
 * Saves the given JSON web token to local storage.
 * If `jwt` is falsy, it is removed from local storage.
 *
 * @param jwt JSON web token to save.
 */
export function saveJwt(jwt?: string) {
  if (jwt) {
    localStorage.setItem(JWT_KEY, jwt);
  } else {
    localStorage.removeItem(JWT_KEY);
  }
}

/**
 * @param iso ISO datetime string.
 * @returns Friendly datetime string.
 */
export function formatIsoToLocaleDateTime(iso: string) {
  return Date.parse(iso)
    ? new Date(iso).toLocaleString('en-GB', {
        weekday: 'short',
        year: '2-digit',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : undefined;
}

/**
 * Quotes a message in markdown based on the given prompt type.
 * @param message Message to be quoted.
 * @param promptType Prompt type to specify additional message.
 * @returns Quoted message.
 */
export function quoteMessage(message: string, promptType?: PromptType) {
  const quoted = `> ${message.trim().split(/\n+/).join('  \n')}`;
  switch (promptType) {
    case 'empathetic_rewrite':
      return `***Rewrite in an empathetic manner***\n\n${quoted}`;
    case 'generate_feedback_CT_supervision':
      return `***Propose feedback (CT-supervision)***\n\n${quoted}`;
    case 'generate_feedback_CCSR':
      return `***Propose feedback (CCS-R) to Care Provider.***`;
    case 'generate_feedback_FIST':
      return `***Propose feedback (FIS-T) to Care Provider.***`;
    case 'generate_feedback_TES':
      return `***Propose feedback (TES) to Care Provider.***`;
    case 'generate_response':
      return `***Propose response** after*\n\n${quoted}`;
    case 'generate_response_conversational':
      return `***Propose conversational response** after*\n\n${quoted}`;
    case 'generate_wrap_up':
      return `***Propose response (wrapping up)** after*\n\n${quoted}`;
    case 'generate_problem_identification':
      return `***Propose PADI response (problem identification)** after*\n\n${quoted}`;
    case 'generate_attempted_solutions':
      return `***Propose PADI response (attempted solutions)** after*\n\n${quoted}`;
    case 'generate_desired_outcome':
      return `***Propose PADI response (desired outcome)** after*\n\n${quoted}`;
    case 'generate_interventions':
      return `***Propose PADI response (interventions)** after*\n\n${quoted}`;
    case 'generate_C3P_guide':
      return `***Propose response (for C3P)** for*\n\n${quoted}`;
    case 'generate_directions':
      return `***Propose directions** after*\n\n${quoted}`;
    case 'reasons_for_living':
      return `***Reasons for Living** after*\n\n${quoted}`;
    case 'recommend_resource':
      return `***Recommend resources.***`;
    case 'summarize_post':
      return `***Summarise post-conversation***\n\n${quoted}`;
    case 'summarize':
      return `***Summarise conversation***\n\n${quoted}`;
    case 'direct':
    default:
      return message;
  }
}

/**
 * Generates a series of rotating color gradients.
 *
 * @param colors Colors to animate between (at least 2).
 * @param deg Degrees to rotate to.
 * @returns Array of `linear-gradient` values.
 */
export function generateGradients(colors: string[], deg = 360) {
  const n = colors.length;
  const interval = deg / n;
  const gradients: string[] = [];
  for (let i = 0; i < n + 1; i += 1) {
    gradients.push(
      `linear-gradient(${i * interval}deg, ${colors[i % n]}, ${
        colors[(i + 1) % n]
      })`
    );
  }
  return gradients;
}

export function capitalizeFirstLetter(str: string | undefined) {
  if (str) {
    const [first, ...rest] = str;
    return first.toUpperCase() + rest.join('');
  }

  return str;
}

export function getStringBetween(str: string, start: string, end: string) {
  if (str) {
    const parts = str.split(start);
    if (parts.length > 1) {
      const subParts = parts[1].split(end);
      return subParts[0];
    }
  }

  return '';
}

export function getSuicidalityLabel(str: SuicideLabel | undefined) {
  if (str) {
    switch (str) {
      case 'LABEL_0':
        return 'Non-suicidal';
      case 'LABEL_1':
        return 'Suicidal';
      default:
        break;
    }
  }
  return '';
}

export function getModels() {
  let models: { tag: ModelTag; label: string }[] = [
    { tag: 'o1-mini', label: 'GPT o1 mini' },
    { tag: 'o3-mini', label: 'GPT o3 mini' },
    { tag: 'gpt-5.2', label: 'GPT-5.2' },
    { tag: 'gpt-5.2-enabled', label: 'GPT-5.2 Enabled Reasoning' },
    { tag: 'gpt-4o', label: 'GPT-4o' },
    { tag: 'gpt-4o-mini', label: 'GPT-4o mini' },
    {
      tag: TRAINED_MODEL_TAG[4.0],
      label: 'GPT-4o mini trained',
    },
    {
      tag: TRAINED_MODEL_TAG[4.1],
      label: 'GPT-4.1 mini trained',
    },
    { tag: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { tag: 'llama3-8b-instruct', label: 'Llama3 8b Instruct' },
    { tag: 'llama3-70b-instruct', label: 'Llama3 70b Instruct' },
    { tag: 'mistral-7b-instruct', label: 'Mistral 7b Instruct' },
    { tag: 'mistral-large', label: 'Mistral Large' },
    { tag: 'mixtral-8x7b-instruct', label: 'Mixtral 8x7b Instruct' },
    {
      tag: 'claude-4.5-sonnet',
      label: 'Claude 4.5 Sonnet',
    },
  ];

  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC') {
    models.push({
      tag: 'aisingapore/Llama-SEA-LION-v3.5-8B-R',
      label: 'Llama-SEA-LION-v3.5-8B',
    });
  }

  return models;
}

export const TRAINED_MODEL_TAG = {
  4.0: 'ft:gpt-4o-mini-2024-07-18:moht:sos-sft-2a:BBF6ERzt',
  4.1: 'ft:gpt-4.1-mini-2025-04-14:moht:sos-sft-mini4-1-realdata2-lessof:BsSPSrj8:ckpt-step-815',
} as const;

export const TRAINED_MODEL_VALUES: readonly string[] =
  Object.values(TRAINED_MODEL_TAG);

export function getFilterLabel(str: string) {
  switch (str) {
    case 'none':
      return 'All chats';
    case 'ongoing':
      return 'Pending Review';
    case 'completed':
      return 'Reviewed';
    default:
      break;
  }

  return '';
}

export function getActorId(str: string) {
  switch (str) {
    case 'sally':
      return 'abused';
    case 'fadli':
      return 'addicted';
    case 'hana':
      return 'body_image';
    case 'jayden':
      return 'bored';
    case 'tom':
      return 'grieving';
    case 'sharifah':
      return 'health_concern';
    case 'claire':
      return 'heartbroken';
    case 'antonio':
      return 'lonely';
    case 'suwen':
      return 'paranoid';
    case 'karen':
      return 'parent';
    case 'anish':
      return 'stressed';
    case 'zach':
      return 'suicidal_medium';
    case 'taro':
      return 'suicidal_high';
    case 'junjie':
      return 'traumatised';
    case 'anna':
      return 'elderly_SOS';
    case 'annabelle':
      return 'grief_SOS';
    case 'edmund':
      return 'alcoholism_SOS';
    case 'emily':
      return 'friendship_rejection_SOS';
    case 'ethan':
      return 'concerned_son_SOS';
    case 'jasmine':
      return 'concerned_girlfriend_SOS';
    case 'jasper':
      return 'non_binary_SOS';
    case 'jeremy':
      return 'concerned_friend_SOS';
    case 'jessica':
      return 'harassment_SOS';
    case 'jovin':
      return 'overwork_SOS';
    case 'kai':
      return 'delusional_SOS';
    case 'matthias':
      return 'borderline_PD_SOS';
    case 'may':
      return 'isolation_SOS';
    case 'nara':
      return 'heartbroken_SOS';
    case 'rilla':
      return 'unemployment_SOS';
    case 'susan':
      return 'caregiver_SOS';
    case 'taylor':
      return 'transgender_SOS';
    case 'winnie':
      return 'teacher_SOS';
    case 'amos':
      return 'mindline_amos';
    case 'alice':
      return 'mindline_alice';
    case 'dwight':
      return 'mindline_dwight';
    default:
      break;
  }

  return '';
}
